<script setup lang="ts">
/**
 * Colour field: a swatch button that opens the palette (NxColorSwatchPicker in
 * a popover) and a hex text field beside it. `v-model` is `#RRGGBB` — or
 * `#RRGGBBAA` with `allowAlpha` — or `null` for none. Typed hex is checked on
 * blur and Enter; an invalid entry shows `invalidMessage` and keeps the last
 * valid colour. `clearable` adds the reset row („Keine Farbe“).
 *
 * Picked colours join the recent list under `recentKey`, shared by every
 * colour field with the same key.
 */
import { computed, ref, useId, watch } from 'vue';
import { NxPopover } from '../Popover';
import NxColorSwatchPicker from './NxColorSwatchPicker.vue';
import { normalizeHex, useNxRecentColors } from './palette';

const props = withDefaults(
  defineProps<{
    label?: string;
    allowAlpha?: boolean;
    clearable?: boolean;
    clearLabel?: string;
    disabled?: boolean;
    placeholder?: string;
    invalidMessage?: string;
    /** Accessible name of the swatch button. */
    pickLabel?: string;
    recentKey?: string;
  }>(),
  {
    label: undefined,
    allowAlpha: false,
    clearable: false,
    clearLabel: 'Keine Farbe',
    disabled: false,
    placeholder: '#RRGGBB',
    invalidMessage: 'Keine gültige Farbe — z. B. #1A73E8',
    pickLabel: 'Farbe wählen',
    recentKey: 'default',
  }
);

const model = defineModel<string | null>({ default: null });

const id = useId();
const errorId = `${id}-error`;
const open = ref(false);
const text = ref(model.value ?? '');
const invalid = ref(false);
const { recent, remember } = useNxRecentColors(props.recentKey);

const color = computed(() => normalizeHex(model.value, props.allowAlpha));

watch(model, (v) => {
  text.value = v ?? '';
  invalid.value = false;
});

function commitText() {
  const raw = text.value.trim();
  if (!raw) {
    if (props.clearable) {
      model.value = null;
      invalid.value = false;
    } else {
      text.value = model.value ?? '';
    }
    return;
  }
  const n = normalizeHex(raw, props.allowAlpha);
  if (!n) {
    invalid.value = true;
    return;
  }
  invalid.value = false;
  text.value = n;
  if (n !== model.value) {
    model.value = n;
    remember(n);
  }
}

function onSelect(hex: string) {
  model.value = hex;
  remember(hex);
  open.value = false;
}

function onReset() {
  model.value = null;
  open.value = false;
}
</script>

<template>
  <div class="nx-color-input" :class="{ 'is-disabled': props.disabled, 'is-invalid': invalid }">
    <label v-if="props.label" class="nx-color-input__label" :for="id">{{ props.label }}</label>
    <div class="nx-color-input__row">
      <NxPopover v-model:open="open" side="bottom-start">
        <template #trigger>
          <button
            type="button"
            class="nx-color-input__swatch"
            :class="{ 'is-empty': !color }"
            :style="color ? { '--swatch-color': color } : undefined"
            :disabled="props.disabled"
            :aria-label="props.pickLabel"
            :title="props.pickLabel"
            data-testid="nx-color-input-swatch"
          />
        </template>
        <NxColorSwatchPicker
          :value="color"
          :recent="recent"
          :reset-label="props.clearable ? props.clearLabel : undefined"
          @select="onSelect"
          @reset="onReset"
        />
      </NxPopover>
      <input
        :id="id"
        v-model="text"
        class="nx-color-input__text"
        type="text"
        spellcheck="false"
        autocomplete="off"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :aria-invalid="invalid || undefined"
        :aria-describedby="invalid ? errorId : undefined"
        data-testid="nx-color-input-text"
        @blur="commitText"
        @keydown.enter.prevent="commitText"
      />
    </div>
    <p v-if="invalid" :id="errorId" class="nx-color-input__error" role="alert">
      {{ props.invalidMessage }}
    </p>
  </div>
</template>

<style scoped>
.nx-color-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-color-input__label {
  color: var(--nxt-label-fg, var(--nxt-fg-subtle));
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-color-input__row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-color-input__swatch {
  flex: 0 0 auto;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  /* A checkerboard under the colour shows transparency and „no colour“. */
  background:
    linear-gradient(var(--swatch-color, transparent), var(--swatch-color, transparent)),
    repeating-conic-gradient(var(--nxt-chart-track) 0 25%, var(--nxt-surface-bg) 0 50%) 50% /
      calc(var(--nxt-size-unit) * 0.5) calc(var(--nxt-size-unit) * 0.5);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg);
  cursor: pointer;
  transition: box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-color-input__swatch.is-empty {
  background:
    linear-gradient(
      to top right,
      transparent calc(50% - var(--nxt-hairline)),
      var(--nxt-danger-bg) calc(50% - var(--nxt-hairline)),
      var(--nxt-danger-bg) calc(50% + var(--nxt-hairline)),
      transparent calc(50% + var(--nxt-hairline))
    ),
    var(--nxt-surface-bg);
}
.nx-color-input__swatch:focus-visible {
  outline: none;
  box-shadow:
    inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-color-input__swatch:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.nx-color-input__text {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(var(--nxt-size-unit) * 8);
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: var(--nxt-font-size-sm);
  text-transform: uppercase;
}
.nx-color-input__text::placeholder {
  color: var(--nxt-fg-muted);
  text-transform: none;
}
.nx-color-input__text:focus-visible {
  outline: none;
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-color-input.is-invalid .nx-color-input__text {
  border-color: var(--nxt-danger-border);
}
.nx-color-input.is-disabled .nx-color-input__text {
  opacity: 0.6;
}
.nx-color-input__error {
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
}
@media (prefers-reduced-motion: reduce) {
  .nx-color-input__swatch {
    transition: none;
  }
}
</style>
